import type { JSX } from 'react';
import { useCallback } from 'react';
import { SectionHeader } from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import { Panel } from '@/components/blocks/data-display';
import { EmptyState } from '@/components/blocks/feedback';
import type { PersonRecord } from '@/data/schemas';
import { formatSiteSequence } from '@/utils/entityIdentity';
import type { PersonnelGroups } from '../types';
import { PersonnelCard } from '../../../components/PersonnelCard';

export function CompanyPersonnelTab({
  personnelGroups,
  onCreatePersonnel,
  onEditPersonnel,
}: {
  personnelGroups: PersonnelGroups;
  onCreatePersonnel: () => void;
  onEditPersonnel: (personnelId: string) => void;
}): JSX.Element {
  const renderPersonnelCard = useCallback(
    function renderPersonnelCard(personnel: PersonRecord) {
      return <PersonnelCard person={personnel} onEdit={onEditPersonnel} />;
    },
    [onEditPersonnel]
  );

  const hasAnyRecords =
    personnelGroups.company.length > 0 ||
    personnelGroups.siteGroups.some((group) => group.personnel.length > 0) ||
    personnelGroups.unassigned.length > 0;
  const hasSites = personnelGroups.siteGroups.length > 0;

  return (
    <Section>
      <SectionHeader
        title="Rubrica personale"
        action={{
          label: 'Nuovo personale',
          onClick: onCreatePersonnel,
        }}
      />

      {!hasAnyRecords && !hasSites ? (
        <Panel>
          <EmptyState
            icon="group"
            title="Nessun personale"
            description="Aggiungi il primo membro del personale per questa azienda."
            action={{ label: 'Nuovo personale', onClick: onCreatePersonnel }}
            variant="inline"
          />
        </Panel>
      ) : (
        <Section spacing="lg">
          <Section spacing="md">
            <GroupHeader
              title="Personale aziendale"
              description="Visibile per tutti gli stabilimenti"
            />
            {personnelGroups.company.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {personnelGroups.company.map(renderPersonnelCard)}
              </div>
            ) : (
              <Panel>
                <EmptyState
                  icon="group_off"
                  title="Nessun contatto aziendale"
                  description="Nessun contatto aziendale registrato."
                  variant="inline"
                />
              </Panel>
            )}
          </Section>

          {personnelGroups.siteGroups.map((group) => {
            const sequenceLabel = formatSiteSequence(group.sequenceNumber);
            const shorthand = group.shorthand ?? sequenceLabel;
            const title = shorthand ? `${group.siteName} (${shorthand})` : group.siteName;
            return (
              <Section key={group.siteId} spacing="md">
                <GroupHeader
                  title={`Stabilimento · ${title}`}
                  description="Disponibile nei report dello stabilimento"
                />
                {group.personnel.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {group.personnel.map(renderPersonnelCard)}
                  </div>
                ) : (
                  <Panel>
                    <EmptyState
                      icon="group_off"
                      title="Nessun contatto assegnato"
                      description="Nessun contatto assegnato a questo stabilimento."
                      variant="inline"
                    />
                  </Panel>
                )}
              </Section>
            );
          })}

          {personnelGroups.unassigned.length > 0 ? (
            <Section spacing="md">
              <GroupHeader
                title="Personale senza sede"
                description="Assegna a uno stabilimento modificando il contatto"
              />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {personnelGroups.unassigned.map(renderPersonnelCard)}
              </div>
            </Section>
          ) : null}
        </Section>
      )}
    </Section>
  );
}

function GroupHeader({ title, description }: { title: string; description?: string }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{title}</p>
        {description ? <p className="text-xs text-text-tertiary">{description}</p> : null}
      </div>
    </div>
  );
}
